import request from '@/api/request';
import { isEmpty } from 'lodash';
import { createModel } from '@rematch/core';
import type { ModelEffectsCreator, RematchRootState } from '@rematch/core';
import type { RootModel, Curve, CurveSettings, CurvesState } from '@/components/WellLoggingOperations/types';

interface CurveSettingsGroup {
    title: string;
    key: string;
    children: CurveSettings[];
}

interface CurveSettingsModelState {
    linkingMode: boolean;
    offset: Record<string, Record<string, number>>;
    data: Record<string, CurveSettings>;
    isLoading: boolean;
    error: string | null;
}

export const curveSettingsModel = createModel<RootModel>()({
    name: 'curveSettings',
    state: {
        linkingMode: false,
        offset: {},
        data: {},
        isLoading: false,
        error: null,
    } as CurveSettingsModelState,
    reducers: {
        setCurveSettings(state, payload: Record<string, CurveSettings>) {
            return { ...state, data: payload, isLoading: false };
        },
        setLinkingMode(state, payload: { enabled: boolean }) {
            return { ...state, linkingMode: payload.enabled };
        },
        setOffset(state, payload: { id: string; value: number; tabletKey: string }) {
            return {
                ...state,
                offset: {
                    ...state.offset,
                    [payload.tabletKey]: {
                        ...(state.offset[payload.tabletKey] || {}),
                        [payload.id]: payload.value,
                    },
                },
            };
        },
        setLoading(state) {
            return { ...state, isLoading: true };
        },
        setError(state, err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            return { ...state, error: message, isLoading: false };
        },
    },
    effects: (dispatch => ({
        async setOffsetEffect(
            payload: { id: string; value: number; tabletKey: string },
            rootState: RematchRootState<RootModel>,
        ) {
            const newCurves: Record<string, Curve> = {};

            Object.keys((rootState.curves as CurvesState).data).forEach(id => {
                let originalId = String(payload.id);
                if (originalId.includes('00001') || originalId.includes('00002')) {
                    originalId = originalId.slice(0, -5);
                }
                if (id.includes(originalId)) {
                    newCurves[id] = {
                        ...(rootState.curves as CurvesState).data[id],
                        y: (rootState.curves as CurvesState).data[id].y.map((y: number) => y + payload.value),
                    };
                }
            });

            dispatch.curves.setCurves({
                ...(rootState.curves as CurvesState).data,
                ...newCurves,
            });

            const newValue = payload.value
                ? ((rootState.curveSettings as CurveSettingsModelState).offset?.[payload.tabletKey]?.[payload.id] ??
                      0) + payload.value
                : 0;

            dispatch.curveSettings.setOffset({
                id: payload.id,
                value: newValue,
                tabletKey: payload.tabletKey,
            });
        },

        async loadCurveSettings(_, rootState: RematchRootState<RootModel>) {
            if (
                !(rootState.curveSettings as CurveSettingsModelState).isLoading &&
                isEmpty((rootState.curveSettings as CurveSettingsModelState).data)
            ) {
                dispatch.curveSettings.setLoading();
                dispatch.tablets.incrementingCurrentRequests();

                try {
                    const result = await request.get(`logging/curve-settings-gis/`);
                    const payload = (await result.json()) as CurveSettingsGroup[];

                    const data = payload.reduce(
                        (res, cur) => {
                            cur.children.forEach(x => {
                                res[x.code] = x;
                            });
                            return res;
                        },
                        {} as Record<string, CurveSettings>,
                    );

                    dispatch.curveSettings.setCurveSettings(data);
                } catch (error) {
                    dispatch.curveSettings.setError(error);
                } finally {
                    dispatch.tablets.decrementingCurrentRequests();
                }
            }
        },
    })) as ModelEffectsCreator<RootModel>,
});
